import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { storage } from './storage'

const DEFAULT_ERROR_MSG = '系统繁忙/异常，请稍后重试'

const pendingControllers = new Set<AbortController>()

function cancelAllRequests() {
  pendingControllers.forEach((c) => c.abort())
  pendingControllers.clear()
}

const request: AxiosInstance = axios.create({
  baseURL: import.meta.env.DEV
    ? ''
    : import.meta.env.VITE_API_BASE_URL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
})

request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = storage.get<string>('token')
    if (token && config.headers) {
      config.headers.Authorization = `${token}`
    }
    if (config.data && typeof config.data === 'object' && !(config.data instanceof URLSearchParams)) {
      config.data = new URLSearchParams(config.data as Record<string, string>)
    }
    const controller = new AbortController()
    config.signal = controller.signal
    pendingControllers.add(controller)
    return config
  },
  (error) => Promise.reject(error),
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function removeDoneController(response: { config?: { signal?: any } }) {
  if (response.config?.signal) {
    const signal = response.config.signal
    pendingControllers.forEach((c) => {
      if (c.signal === signal) pendingControllers.delete(c)
    })
  }
}

request.interceptors.response.use(
  async (response) => {
    removeDoneController(response)
    if (response.config.responseType === 'blob') {
      return response.data
    }
    const res = response.data
    if (res.code === 200) {
      return res.data
    }
    if (res.code === 400001) {
      cancelAllRequests()
      storage.remove('token')
      storage.remove('userInfo')
      // 动态导入避免循环依赖，清 Pinia 状态后用 router 跳转登录页
      const [{ useAuthStore }, { usePermissionStore }, { default: router }] = await Promise.all([
        import('@/stores/modules/auth'),
        import('@/stores/modules/permission'),
        import('@/router'),
      ])
      const authStore = useAuthStore()
      authStore.token = ''
      authStore.userInfo = null
      usePermissionStore().resetRoutes()
      const currentPath = window.location.hash.replace(/^#/, '') || '/'
      router.push({ path: '/login', query: { redirect: currentPath } })
      return Promise.reject(new Error(res.message || '登录已过期'))
    }
    const msg = res.message || DEFAULT_ERROR_MSG
    ElMessage.error(msg)
    return Promise.reject(new Error(msg))
  },
  (error) => {
    if (!axios.isCancel(error)) {
      removeDoneController(error.response ?? {})
    }
    if (axios.isCancel(error)) {
      return Promise.reject(error)
    }
    if (error.response) {
      const status = error.response.status
      const messageMap: Record<number, string> = {
        401: '登录已过期，请重新登录',
        403: '没有操作权限',
        404: '请求的资源不存在',
        500: '服务器正在维护，请稍等片刻',
      }
      const msg = messageMap[status] ?? error.message ?? DEFAULT_ERROR_MSG
      ElMessage.error(msg)
    } else {
      ElMessage.error(DEFAULT_ERROR_MSG)
    }
    return Promise.reject(error)
  },
)

export function post<T>(url: string, data?: object, config?: AxiosRequestConfig): Promise<T> {
  return request.post(url, data, config)
}

export default request
